import { asArray, formatCurrency, formatDate, packageNetPrice, packageRsp } from "../../lib/catalog";
import { packageSeatSummary } from "../../lib/packageSeats";

const EMPTY_VALUES = new Set(["", "null", "undefined", "n/a", "na", "not set", "not provided"]);

export const PACKAGE_DETAIL_TABS = [
  { id: "overview", label: "Overview" },
  { id: "flight", label: "Flight" },
  { id: "hotels", label: "Hotels" },
  { id: "itinerary", label: "Itinerary" },
  { id: "inclusions", label: "Inclusions" },
  { id: "exclusions", label: "Exclusions" },
  { id: "gallery", label: "Gallery" },
  { id: "pricing", label: "Pricing" },
];

const PACKAGE_CITY_NAMES = {
  278: "Madinah",
  279: "Makkah",
  280: "Madinah",
  MAK: "Makkah",
  MAD: "Madinah",
  MED: "Madinah",
  MECCA: "Makkah",
  MAKKAH: "Makkah",
  MADINAH: "Madinah",
  MADINA: "Madinah",
};

export function resolveCityName(value) {
  if (!hasValue(value)) return "";
  const text = String(value).trim();
  const mapped = PACKAGE_CITY_NAMES[text] || PACKAGE_CITY_NAMES[text.toUpperCase()] || PACKAGE_CITY_NAMES[Number(text)];
  if (mapped) return mapped;
  if (/^\d+$/.test(text)) return "";
  return text;
}

export function hasValue(value) {
  if (value == null) return false;
  const text = String(value).trim().toLowerCase();
  return text !== "" && !EMPTY_VALUES.has(text);
}

export function firstValue(source, keys = []) {
  for (const key of keys) {
    if (hasValue(source?.[key])) return source[key];
  }
  return null;
}

export function displayValue(value, fallback = "") {
  return hasValue(value) ? value : fallback;
}

export function getDataShape(payload) {
  const data = payload?.data || payload || {};
  const shaped = data.package ? data : { package: data };
  const pkg = shaped.package || {};

  return {
    package: pkg,
    hotels: asArray(shaped.hotels || pkg.hotels),
    itinerary: asArray(shaped.itinerary || pkg.itinerary),
    gallery: asArray(shaped.gallery || pkg.gallery),
    flightBlock: shaped.flightBlock || pkg.flightBlock || pkg.flight?.block || null,
    flightBlockDetails: asArray(
      shaped.flightBlockDetails || pkg.flightBlockDetails || pkg.flight?.details
    ),
  };
}

export function occupancyRates(pkg = {}) {
  const rows = [
    { label: "Single Sharing", icon: "solar:user-linear", netKey: "single", publicKey: "perAdultSingle", rspKey: "perAdultSingleRsp" },
    { label: "Twin Sharing", icon: "solar:users-group-rounded-linear", netKey: "twin", publicKey: "perAdultTwin", rspKey: "perAdultTwinRsp" },
    { label: "Triple Sharing", icon: "solar:users-group-two-rounded-linear", netKey: "triple", publicKey: "perAdultTriple", rspKey: "perAdultTripleRsp" },
    { label: "Quad Sharing", icon: "solar:users-group-rounded-bold", netKey: "quad", publicKey: "perAdultQuad", rspKey: "perAdultQuadRsp" },
    { label: "Quint Sharing", icon: "solar:user-plus-linear", netKey: "quint", publicKey: "perAdultQuint", rspKey: "perAdultQuintRsp" },
    { label: "Hexa Sharing", icon: "solar:users-group-rounded-bold", netKey: "hexa", publicKey: "perAdultHexa", rspKey: "perAdultHexaRsp" },
    { label: "Child with bed", icon: "solar:bed-linear", netKey: "childWithBed", publicKey: "perChild6to11", rspKey: "perChild6to11Rsp" },
    { label: "Child without bed", icon: "solar:user-rounded-linear", netKey: "childWithoutBed", publicKey: "perChild2to6", rspKey: "perChild2to6Rsp" },
    { label: "Infant", icon: "solar:smile-circle-linear", netKey: "infant", publicKey: "perInfant", rspKey: "perInfantRsp" },
  ];

  return rows.map((row) => {
    const net = pkg.shareRates?.[row.netKey] ?? pkg[row.publicKey];
    const rsp = pkg.shareRspRates?.[row.netKey] ?? pkg[row.rspKey] ?? net;
    return { label: row.label, icon: row.icon, net, rsp };
  });
}

export function formatDuration(pkg = {}) {
  if (hasValue(pkg.duration)) {
    const value = String(pkg.duration).trim();
    return /day/i.test(value) ? value : `${value} days`;
  }

  const parts = [];
  if (hasValue(pkg.days)) parts.push(`${pkg.days} days`);
  if (hasValue(pkg.nights)) parts.push(`${pkg.nights} nights`);
  return parts.length ? parts.join(" / ") : "";
}

export function packageFacts(pkg = {}) {
  const seats = packageSeatSummary(pkg);
  return [
    { label: "Departure", value: formatDate(pkg.departureDate) },
    { label: "Return", value: formatDate(pkg.returnDate) },
    { label: "Duration", value: formatDuration(pkg) },
    { label: "Available seats", value: seats.total ? `${seats.available} / ${seats.total}` : "" },
    { label: "Package type", value: pkg.category || pkg.tourType || pkg.pkg_tour_category },
    { label: "Net price", value: formatCurrency(packageNetPrice(pkg)) },
    { label: "RSP", value: formatCurrency(packageRsp(pkg)) },
  ];
}

export function looksLikeId(value) {
  return /^\d+$/.test(String(value || "").trim());
}

export function preferName(...values) {
  for (const value of values) {
    if (hasValue(value) && !looksLikeId(value)) return value;
  }
  return "";
}

function pad2(value) {
  return String(value).padStart(2, "0");
}

export function formatFlightDate(value) {
  if (!hasValue(value)) return "";
  const text = String(value).trim();
  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}-${iso[2]}-${iso[1]}`;
  const dmy = text.match(/^(\d{2})-(\d{2})-(\d{4})/);
  if (dmy) return text.slice(0, 10);

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${pad2(date.getDate())}-${pad2(date.getMonth() + 1)}-${date.getFullYear()}`;
}

export function formatFlightTime(value) {
  if (!hasValue(value)) return "";
  const text = String(value).trim();
  const match = text.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (match) return `${pad2(match[1])}:${match[2]}`;

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

export function formatFlightDateTime(dateValue, timeValue) {
  const datePart = formatFlightDate(dateValue);
  const timePart = formatFlightTime(timeValue) || formatFlightTime(dateValue);
  return [datePart, timePart].filter(Boolean).join(" ");
}

export function normalizeFlightSummary(pkg = {}, flightBlock = null, legs = []) {
  const packageFlight = pkg.packageFlight || {};
  const block = flightBlock || {};
  const firstAirline = legs.find((leg) => preferName(leg.airline))?.airline || "";

  return {
    name: preferName(
      packageFlight.name,
      block.name,
      block.fb_name,
      block.block_name,
      firstAirline
    ),
    status: firstValue(packageFlight, ["status"]) || firstValue(block, ["status", "fb_status"]),
    adultPrice: firstValue(packageFlight, ["adultPrice", "adult_price"]) || firstValue(block, ["adultPrice", "fb_adult_price", "adult_price"]),
    seats: firstValue(packageFlight, ["seats"]) ?? firstValue(block, ["seats", "fb_seat", "available_seats"]),
    pnr: firstValue(packageFlight, ["pnr"]) || firstValue(block, ["pnr", "fb_pnr"]),
    code: firstValue(block, ["code", "fb_code", "block_code"]),
  };
}

export function hasFlightData(summary, legs = []) {
  const price = Number(summary?.adultPrice);
  const seats = Number(summary?.seats);
  return Boolean(
    legs.length ||
      hasValue(summary?.name) ||
      hasValue(summary?.status) ||
      hasValue(summary?.pnr) ||
      hasValue(summary?.code) ||
      (hasValue(summary?.seats) && seats !== 0) ||
      (Number.isFinite(price) && price > 0)
  );
}

export function normalizeFlightLeg(item = {}, index = 0) {
  const isReturn = String(item.leg || "").toLowerCase().includes("return");
  return {
    id: firstValue(item, ["id", "fbo_id", "fbr_id"]) || `leg-${index}`,
    leg: isReturn ? "return" : "onward",
    airline: preferName(
      item.airlineName,
      item.fam_airline_name,
      item.fbo_airline_name,
      item.airlines,
      item.airline,
      item.fbo_airlines,
      item.fbr_airlines
    ),
    code: firstValue(item, ["flightNumber", "fbo_flight_id", "fbr_flight_id", "flightId", "flight_id", "code", "fbo_code", "fbr_code", "flight_code"]),
    travelClass: firstValue(item, ["class", "fbo_class", "fbr_class", "flight_class"]),
    stops: firstValue(item, ["stops", "fbo_stops", "fbr_stops"]),
    departure: preferName(
      item.departure_airport_name,
      item.departureName,
      item.fbo_departure_name,
      item.departure,
      item.fbo_departure,
      item.fbr_departure,
      item.departure_city
    ),
    arrival: preferName(
      item.arrival_airport_name,
      item.arrivalName,
      item.fbo_arrival_name,
      item.arrival,
      item.fbo_arrival,
      item.fbr_arrival,
      item.arrival_city
    ),
    departureAt: formatFlightDateTime(
      firstValue(item, ["departureDate", "fbo_departure_date", "fbr_departure_date", "departure_date"]),
      firstValue(item, ["departureTime", "fbo_departure_time", "fbr_departure_time", "departure_time"])
    ),
    arrivalAt: formatFlightDateTime(
      firstValue(item, ["arrivalDate", "fbo_arrival_date", "fbr_arrival_date", "arrival_date"]),
      firstValue(item, ["arrivalTime", "fbo_arrival_time", "fbr_arrival_time", "arrival_time"])
    ),
  };
}

export function normalizeHotel(hotel = {}, index = 0) {
  const checkIn = firstValue(hotel, ["checkIn", "check_in", "checkin"]);
  const checkOut = firstValue(hotel, ["checkOut", "check_out", "checkout"]);

  return {
    id: firstValue(hotel, ["id", "uphp_id"]) || `hotel-${index}`,
    city: resolveCityName(
      firstValue(hotel, ["cityName", "city_name", "city", "uphp_city", "location"])
    ),
    name: firstValue(hotel, ["hotel", "hotelName", "uphp_hotel", "name", "uhl_hotel_name"]),
    nights: firstValue(hotel, ["nights", "uphp_no_of_night", "no_of_nights", "noOfNight"]),
    checkIn: checkIn ? formatDate(checkIn) : "",
    checkOut: checkOut ? formatDate(checkOut) : "",
    image: firstValue(hotel, ["hotelImage", "image", "uhl_image", "photo"]),
  };
}

export function hotelHasContent(hotel = {}) {
  return Boolean(
    hasValue(hotel.name) ||
      hasValue(hotel.city) ||
      hasValue(hotel.nights) ||
      hotel.checkIn ||
      hotel.checkOut ||
      hotel.image
  );
}

export function groupHotelsByCity(hotels = []) {
  const groups = [];
  const byCity = new Map();

  hotels.forEach((hotel) => {
    const city = hotel.city || "";
    if (!byCity.has(city)) {
      const group = { city, hotels: [] };
      byCity.set(city, group);
      groups.push(group);
    }
    byCity.get(city).hotels.push(hotel);
  });

  return groups;
}

export function normalizeItineraryItem(item = {}, index = 0) {
  return {
    id: firstValue(item, ["u_pkg_itnry_id", "id"]) || `day-${index + 1}`,
    day: firstValue(item, ["day", "u_pkg_itry_day", "day_number", "day_label", "sort_order"]) || index + 1,
    city: resolveCityName(firstValue(item, ["city", "u_pkg_itry_city"])),
    title: firstValue(item, ["header", "u_pkg_itry_header", "title"]),
    description: firstValue(item, ["text", "u_pkg_itry_text", "description", "notes"]) || "",
  };
}

export function galleryFileName(item) {
  if (typeof item === "string") return item;
  return firstValue(item, ["name", "gallery_name", "fileName", "image"]) || "";
}

export function listItems(value) {
  if (Array.isArray(value)) return value.map((item) => String(item).trim()).filter(Boolean);
  if (!hasValue(value)) return [];
  return String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}
